/**
 * Production's pre-TRUS-762 version, kept verbatim for orgs WITHOUT the
 * `member_profile_v2` capability (they keep the previous member profile).
 * Do not add features here; remove once every org is on member_profile_v2.
 */
/**
 * Member Engagement Intelligence tab.
 *
 * Single read of /api/engagement-intelligence/orgs/{cbo}/members/{id}/profile
 * which pools every signal (communications, case-mgmt touches, care
 * responses, workflow activity, NL responses, workflow instances, service
 * referrals, retention ML) PLUS a computed metrics block. This component
 * renders the metrics + a recent-activity timeline so the member profile
 * has a single "what is happening with this member?" surface.
 */
import React, { useEffect, useState } from 'react';
import { authFetchJson } from '../../lib/authFetch';
import {
  Activity, MessageSquare, Phone, Mail, Target, Users,
  TrendingUp, AlertCircle, CheckCircle, ExternalLink,
} from 'lucide-react';

interface Metrics {
  total_interactions: number;
  communications_sent: number;
  communications_responded: number;
  response_rate_pct: number;
  case_management_touches: number;
  care_responses_completed: number;
  nl_classifications: number;
  surveys_completed: number;
  workflows_total: number;
  workflows_active: number;
  workflows_completed: number;
  workflow_step_completion_rate_pct: number;
  referrals_total: number;
  referrals_completed: number;
  referral_completion_rate_pct: number;
  last_engagement_at: string | null;
  engagement_health: null | {
    score: number | null;
    risk_level: string | null;
    needs_attention: boolean | null;
    last_calculated_at: string | null;
  };
}

interface Profile {
  member_id: number;
  cbo_id: number;
  metrics: Metrics;
  workflow_instances: any[];
  service_referrals: any[];
  case_management_touches: any[];
  care_responses: any[];
  nl_responses: { intent_classifications: any[]; surveys: any[] };
  communications: { totals: Record<string, number>; by_dimension: any[] };
}

const RISK_COLOR: Record<string, string> = {
  healthy:  'bg-emerald-100 text-emerald-800',
  watch:    'bg-amber-100 text-amber-800',
  at_risk:  'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const MetricCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}> = ({ label, value, sub, icon: Icon, tone = 'default' }) => {
  const toneClass =
    tone === 'good' ? 'text-emerald-700' :
    tone === 'warn' ? 'text-amber-700' :
    tone === 'bad'  ? 'text-red-700' :
                       'text-gray-900';
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
};

export const MemberEngagementIntelligenceTab: React.FC<{
  memberId: number;
  cboId: number;
}> = ({ memberId, cboId }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!memberId || !cboId) return;
    setLoading(true);
    setError(null);
    authFetchJson<Profile>(`/api/engagement-intelligence/orgs/${cboId}/members/${memberId}/profile`)
      .then(setProfile)
      .catch(e => setError(e?.message || 'Failed to load engagement intelligence'))
      .finally(() => setLoading(false));
  }, [memberId, cboId]);

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading engagement intelligence…</div>;
  if (error)   return <div className="p-6 text-sm text-red-600">{error}</div>;
  if (!profile) return null;

  const m = profile.metrics;
  const health = m.engagement_health;
  const riskClass = health?.risk_level ? RISK_COLOR[health.risk_level] || 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-700';

  return (
    <div className="space-y-6">
      {/* Header + retention health */}
      <div className="rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Member Engagement Intelligence</h3>
            <p className="text-xs text-gray-600">
              Pooled view of every interaction, workflow, and referral on file. Last engagement:{' '}
              {m.last_engagement_at ? new Date(m.last_engagement_at).toLocaleString() : '—'}
            </p>
          </div>
          {health && health.score != null && (
            <div className="text-right">
              <div className="text-xs text-gray-500">Retention health</div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-gray-900">{Math.round(health.score)}</span>
                {health.risk_level && (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${riskClass}`}>
                    {health.risk_level}
                  </span>
                )}
              </div>
              {health.needs_attention && (
                <div className="mt-1 flex items-center justify-end gap-1 text-xs text-orange-700">
                  <AlertCircle className="h-3 w-3" /> Needs attention
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Metrics grid */}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-gray-900">Engagement metrics</h4>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <MetricCard label="Total interactions" value={m.total_interactions} icon={Activity} />
          <MetricCard
            label="Response rate"
            value={`${m.response_rate_pct}%`}
            sub={`${m.communications_responded} responded of ${m.communications_sent} sent`}
            icon={MessageSquare}
            tone={m.response_rate_pct >= 30 ? 'good' : m.response_rate_pct >= 15 ? 'warn' : 'bad'}
          />
          <MetricCard
            label="Case touches"
            value={m.case_management_touches}
            icon={Phone}
          />
          <MetricCard
            label="Care responses"
            value={m.care_responses_completed}
            sub="assessments completed"
            icon={CheckCircle}
            tone="good"
          />
          <MetricCard
            label="Workflows"
            value={`${m.workflows_active} active / ${m.workflows_total}`}
            sub={`${m.workflow_step_completion_rate_pct}% step completion`}
            icon={Target}
          />
          <MetricCard
            label="Referrals"
            value={`${m.referrals_completed} / ${m.referrals_total}`}
            sub={`${m.referral_completion_rate_pct}% completed`}
            icon={Users}
            tone={m.referral_completion_rate_pct >= 50 ? 'good' : 'warn'}
          />
          <MetricCard
            label="NL classifications"
            value={m.nl_classifications}
            sub="inbound intents"
            icon={Mail}
          />
          <MetricCard
            label="Surveys"
            value={m.surveys_completed}
            icon={TrendingUp}
          />
        </div>
      </div>

      {/* Recent referrals */}
      {profile.service_referrals.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-900">
            Recent referrals ({profile.service_referrals.length})
          </h4>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Origin</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {profile.service_referrals.slice(0, 8).map((r: any) => (
                  <tr key={r.id} className="text-xs">
                    <td className="px-3 py-1.5 font-mono text-gray-600">{r.referral_number || r.id}</td>
                    <td className="px-3 py-1.5">{r.service_category_code || r.service_type_code || '—'}</td>
                    <td className="px-3 py-1.5">{r.member_request_type || r.source_type || 'manual'}</td>
                    <td className="px-3 py-1.5">{r.status}</td>
                    <td className="px-3 py-1.5 text-gray-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent workflow instances */}
      {profile.workflow_instances.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-900">
            Recent workflows ({profile.workflow_instances.length})
          </h4>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-left">Progress</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Referral</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {profile.workflow_instances.slice(0, 8).map((w: any) => (
                  <tr key={w.id} className="text-xs">
                    <td className="px-3 py-1.5">{w.template_name || `Template #${w.template_id}`}</td>
                    <td className="px-3 py-1.5">{w.trigger_type ? w.trigger_type.replace(/_/g, ' ') : '—'}</td>
                    <td className="px-3 py-1.5">{w.current_step}/{w.total_steps}</td>
                    <td className="px-3 py-1.5">{w.status || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-600">
                      {w.referral_number || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent intent classifications */}
      {profile.nl_responses.intent_classifications.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-900">
            Recent inbound intents ({profile.nl_responses.intent_classifications.length})
          </h4>
          <div className="space-y-2">
            {profile.nl_responses.intent_classifications.slice(0, 5).map((ic: any) => (
              <div key={ic.id} className="rounded-md border border-gray-200 bg-white p-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold uppercase text-blue-700">
                    {ic.intent_label}
                  </span>
                  <span className="text-gray-500">
                    {ic.confidence != null ? `${Math.round(ic.confidence * 100)}% confidence` : ''}
                  </span>
                  <span className="ml-auto text-gray-400">
                    {ic.created_at ? new Date(ic.created_at).toLocaleString() : ''}
                  </span>
                </div>
                <div className="mt-1 text-gray-700">"{ic.english_text || ic.original_text}"</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberEngagementIntelligenceTab;
